import React, { useEffect, useRef, useState } from 'react'
import JwtDecode from 'jwt-decode'
import { parsePhoneNumber } from 'react-phone-number-input'
import MainForm from './MainForm'
import OtpForm from './OtpForm'
import { notification } from '../common/Notification'
import { IParsedAccessJWT, LOCAL_STORAGE_ACCESS_TOKEN_KEY, LOCAL_STORAGE_REFRESH_TOKEN_KEY } from 'src/utils/auth'
import { RIDER_PORTAL_APP_TYPE, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'
import { IRequestDto, IRiderAuthDetails, IUserDeviceToken } from 'src/types/rider-portal'

interface ILoginProps {
  generateOneTimePassword: (riderDetails: IRiderAuthDetails) => Promise<IRequestDto>
  getCurrentUserInfo: (userId: string) => Promise<void>
  updateDeviceToken: (deviceToken: IUserDeviceToken) => Promise<void>
  verifyOtpCode: (riderDetails: IRiderAuthDetails) => Promise<IRequestDto>
}

export interface IFormValues {
  phoneNumber: {
    countryCode: string
    completePhoneNumber: string // countryCode + phoneNumber
    phoneNumber: string
  }
  otp: string[]
  otpRecipient: 'sms' | 'email'
  persistLogin: boolean
}

export interface IInputData {
  name: string
  value: string | boolean
}

const MAX_LOGIN_TRIES = 5
const OTP_LENGTH = 4

const Login = ({ generateOneTimePassword, getCurrentUserInfo, updateDeviceToken, verifyOtpCode }: ILoginProps) => {
  const [allowLogin, setAllowLogin] = useState(true)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const [formValues, setFormValues] = useState<IFormValues>({
    otp: [],
    otpRecipient: 'sms',
    phoneNumber: {
      countryCode: '',
      completePhoneNumber: '',
      phoneNumber: ''
    },
    persistLogin: false
  })
  const [isResendingOtpReq, setResendingOtpReq] = useState(false)
  const [loading, setLoading] = useState(false)
  const [phoneValid, setPhoneValid] = useState(false)
  const [remainingTries, setRemainingTries] = useState(MAX_LOGIN_TRIES)

  const mountedRef = useRef<boolean>(false)
  const { accessYourAccount, signinToAccount, welcomeBack } = RIDER_PORTAL_UI_MESSAGES.auth
  const RIDER_PORTAL_LOGIN_RESTRICTIONS = 'RIDER_PORTAL_LOGIN_RESTRICTIONS'
  const RIDER_PORTAL_PERSIST_LOGIN = 'RIDER_PORTAL_PERSIST_LOGIN'

  useEffect(() => {
    mountedRef.current = true
    let storedRestrictions: string | null = null
    let isLoginPersistent: string | null = null

    try {
      storedRestrictions = localStorage.getItem(RIDER_PORTAL_LOGIN_RESTRICTIONS)
      if (storedRestrictions) {
        const restrictions = JSON.parse(storedRestrictions)
        const endTime = new Date(restrictions.endTimeOfRestriction)
        const isStillRestricted = new Date() < endTime

        setAllowLogin(!isStillRestricted)
        if (!isStillRestricted) {
          localStorage.removeItem(RIDER_PORTAL_LOGIN_RESTRICTIONS)
        }
      }
    } catch (error) {
      console.log('Error accessing user auth restriction data.')
    }

    try {
      isLoginPersistent = localStorage.getItem(RIDER_PORTAL_PERSIST_LOGIN)
      if (isLoginPersistent) {
        const loginData = JSON.parse(isLoginPersistent)
        const { expiry, phoneNumber } = loginData
        const isStillPersistent = new Date() < new Date(expiry)

        if (isStillPersistent) {
          setFormValues(prev => ({ ...prev, phoneNumber, persistLogin: true }))
        } else {
          localStorage.removeItem(RIDER_PORTAL_PERSIST_LOGIN)
        }
      }
    } catch (error) {
      console.log('Error accessing user auth data.')
    }

    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!allowLogin) {
      notification({ color: 'danger', message: 'Maximum attempts reached. Please try again later.' })
    }
  }, [allowLogin])

  useEffect(() => {
    const {
      otp,
      phoneNumber: { countryCode, phoneNumber }
    } = formValues
    const invalidValues = otp.filter(value => value === '' || value === undefined)

    if (otp.length === OTP_LENGTH && invalidValues.length === 0) {
      if (mountedRef.current) {
        setLoading(true)
        const token = otp.join('')
        const payload = { appType: RIDER_PORTAL_APP_TYPE, countryCode, phoneNumber, token }

        verifyOtpCode(payload)
          .then(({ message, success }) => {
            if (success) {
              const { deviceToken, userId } = getDeviceTokenPayload()
              if (deviceToken && userId) {
                updateDeviceToken({ deviceToken, userId })
                getCurrentUserInfo(String(userId))
              }
            }
            notification({ color: success ? 'success' : 'danger', message })
            validateLoginAttempts(success)
          })
          .catch(({ message, success }) => {
            notification({ color: success ? 'success' : 'danger', message })
            validateLoginAttempts(success)
          })
          .finally(() => setLoading(false))
      }
    }
  }, [formValues.otp])

  useEffect(() => {
    if (isResendingOtpReq) {
      handleSendOtp()
      setResendingOtpReq(false)
    }
  }, [isResendingOtpReq])

  const getDeviceTokenPayload = () => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY) || ''
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY) || ''
    let user: IParsedAccessJWT | null = null
    try {
      user = JwtDecode(accessToken)
      return {
        deviceToken: refreshToken,
        userId: user ? user.id : ''
      }
    } catch {
      return { deviceToken: refreshToken, userId: '' }
    }
  }

  const validateLoginAttempts = (success: boolean) => {
    if (!success) {
      handleFailedAttempt()
    } else {
      handleSuccessfulAttempt()
    }
  }

  const handleFailedAttempt = () => {
    setFocusedIndex(0)
    setFormValues(prev => ({ ...prev, otp: [] }))
    setRemainingTries(prev => {
      if (prev > 1) {
        return prev - 1
      } else {
        disableRequestingOtp()
        return 0
      }
    })
  }

  const handleSuccessfulAttempt = () => {
    const { persistLogin } = formValues
    persistLogin ? enablePersistLogin() : disablePersistLogin()
    setRemainingTries(MAX_LOGIN_TRIES)
  }

  const handleInputChange = ({ name, value }: IInputData) => {
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handlePhoneNumberChange = (value: string) => {
    if (value) {
      const parsedPhoneNumber = parsePhoneNumber(value)
      if (parsedPhoneNumber) {
        const {
          countryCallingCode: countryCode,
          nationalNumber: phoneNumber,
          number: completePhoneNumber
        } = parsedPhoneNumber
        if (countryCode && phoneNumber && completePhoneNumber) {
          setFormValues(prev => ({
            ...prev,
            phoneNumber: { countryCode, completePhoneNumber, phoneNumber }
          }))
        }
      }
    }
  }

  const handleOtpChange = (value: string, index: number) => {
    if (value.split('').length < 2) {
      const newValues = [...formValues.otp]
      newValues[index] = value
      setFormValues(prev => ({ ...prev, otp: newValues }))

      if (value && index < OTP_LENGTH - 1) {
        setFocusedIndex(index + 1)
      }
    }
  }

  const parseLoginData = () => {
    try {
      return localStorage.getItem(RIDER_PORTAL_PERSIST_LOGIN)
    } catch {
      return null
    }
  }

  const enablePersistLogin = () => {
    // Check if there is already persistent login data to prevent overriding the original.
    const isLoginPersistent = parseLoginData()
    if (!isLoginPersistent) {
      const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000
      const persistLoginData = {
        expiry: new Date(Date.now() + THIRTY_DAYS_IN_MS).toISOString(),
        phoneNumber: formValues.phoneNumber,
        remember: true
      }
      localStorage.setItem(RIDER_PORTAL_PERSIST_LOGIN, JSON.stringify(persistLoginData))
    }
  }

  const disablePersistLogin = () => {
    localStorage.removeItem(RIDER_PORTAL_PERSIST_LOGIN)
  }

  const disableRequestingOtp = () => {
    const ONE_HOUR_IN_MS = 60 * 60 * 1000
    const restrictionData = { endTimeOfRestriction: new Date(Date.now() + ONE_HOUR_IN_MS).toISOString() }
    setAllowLogin(false)
    setPhoneValid(false)
    localStorage.setItem(RIDER_PORTAL_LOGIN_RESTRICTIONS, JSON.stringify(restrictionData))
  }

  const handleSendOtp = () => {
    if (mountedRef.current) {
      setLoading(true)
      const {
        otp,
        otpRecipient,
        phoneNumber: { countryCode, phoneNumber }
      } = formValues

      const payload = {
        countryCode,
        otpRecipient,
        phoneNumber
      }

      if (otp.length > 0) {
        setFormValues(prev => ({ ...prev, otp: [] }))
        setFocusedIndex(0)
      }

      generateOneTimePassword(payload)
        .then(({ message, success }) => {
          notification({ color: success ? 'success' : 'danger', message })
          if (!isResendingOtpReq) {
            setPhoneValid(success)
          }
        })
        .catch(({ message, success }) => {
          notification({ color: success ? 'success' : 'danger', message })
          if (!isResendingOtpReq) {
            setPhoneValid(false)
          }
        })
        .finally(() => setLoading(false))
    }
  }

  return (
    <React.Fragment>
      <div className='mbsc-grid'>
        <div className='mbsc-row'>
          <div className='mbsc-col-12 flex mbsc-justify-content-center'>
            <div className='rider-portal-main-container flex mbsc-justify-content-center'>
              <div className='login-form-container py3'>
                <div className='mb3 text-center'>
                  <h1 className='font-bold font-black1 mb2 mt0 text-3xl'>{welcomeBack}</h1>
                  <p className='font-grey7 mx3 my0'>{accessYourAccount}</p>
                </div>
                <div className='login-form p3'>
                  <h2 className='font-bold font-black1 mb2 mt0 text-center text-2xl'>{signinToAccount}</h2>
                  {!phoneValid ? (
                    <MainForm
                      allowLogin={allowLogin}
                      formValues={formValues}
                      loading={loading}
                      handleInputChange={handleInputChange}
                      handlePhoneNumberChange={handlePhoneNumberChange}
                      handleSelectOtpRecipient={recipient =>
                        setFormValues(prev => ({ ...prev, otpRecipient: recipient }))
                      }
                      handleSendOtp={handleSendOtp}
                    />
                  ) : (
                    <div className='mt1 w100'>
                      <OtpForm
                        allowLogin={allowLogin}
                        focusedIndex={focusedIndex}
                        formValues={formValues}
                        loading={loading}
                        remainingTries={remainingTries}
                        handleInputChange={handleInputChange}
                        handleResendOtp={() => setResendingOtpReq(true)}
                        handleOtpChange={handleOtpChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Login



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/authentication/Login.tsx